/**
 * aiChatStore — Zustand persisted store for AI chat sessions.
 *
 * Each session is keyed by a unique string (e.g. "candidate_{appId}" or "job_position_{posId}").
 * Sessions survive page refresh, navigation, and browser restarts (localStorage).
 *
 * TTL logic: if `lastInteractionAt` is older than AI_CHAT_CONFIG.SESSION_TIMEOUT_HOURS,
 * the session is treated as expired and automatically reset to a fresh conversation.
 *
 * Session key conventions:
 *   candidate_{applicationId}         — ApplicationDetail candidate chatbot
 *   job_position_{positionId|'new'}   — JobPositions / PositionDetail edit dialog
 *   floating_position_{positionId}    — PositionDetail floating chat
 *   applications_{companyId}          — ApplicationsPage pipeline assistant
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AiChatMessage, AiConversationState, AiAutoFillSuggestions } from '@/types';
import { AI_CHAT_CONFIG } from '@/config/aiChat';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Generic chat session — used for candidate, floating, and applications chats */
export interface ChatSession {
  messages: { role: 'user' | 'ai'; text: string }[];
  conversationState: AiConversationState | null;
  /** ISO timestamp of the last user/AI message */
  lastInteractionAt: string | null;
  /** Whether the chat panel is open — persisted so it survives tab switches */
  isOpen: boolean;
  /** Whether the chat panel is expanded — persisted so it survives tab switches */
  isExpanded: boolean;
}

/** Job position chat session — includes autofill state */
export interface JobPositionChatSession {
  messages: AiChatMessage[];
  conversationState: AiConversationState | undefined;
  autoFillReady: boolean;
  autoFillSuggestions: AiAutoFillSuggestions | null;
  /** ISO timestamp of the last user/AI message */
  lastInteractionAt: string | null;
}

// Keep backward-compat alias
export type CandidateChatSession = ChatSession;

interface AiChatState {
  /** Generic sessions: candidate_{appId}, floating_position_{posId}, applications_{companyId} */
  sessions: Record<string, ChatSession>;
  /** Job position sessions: job_position_{posId|'new'} */
  jobPositionSessions: Record<string, JobPositionChatSession>;

  // --- Generic session actions ---
  getSession: (key: string) => ChatSession;
  setSession: (key: string, patch: Partial<ChatSession>) => void;
  clearSession: (key: string) => void;

  // --- Backward-compat aliases (candidate) ---
  getCandidateSession: (key: string) => ChatSession;
  setCandidateSession: (key: string, patch: Partial<ChatSession>) => void;
  clearCandidateSession: (key: string) => void;

  // --- Job position chat actions ---
  getJobPositionSession: (key: string) => JobPositionChatSession;
  setJobPositionSession: (key: string, patch: Partial<JobPositionChatSession>) => void;
  clearJobPositionSession: (key: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const EMPTY_SESSION: ChatSession = {
  messages: [],
  conversationState: null,
  lastInteractionAt: null,
  isOpen: false,
  isExpanded: false,
};

export const EMPTY_JOB_POSITION_SESSION: JobPositionChatSession = {
  messages: [],
  conversationState: undefined,
  autoFillReady: false,
  autoFillSuggestions: null,
  lastInteractionAt: null,
};

/**
 * Returns true if the session has expired based on SESSION_TIMEOUT_HOURS.
 * A session with no lastInteractionAt is considered fresh (not expired).
 * Exported so hooks can use it in reactive Zustand selectors.
 */
export function isSessionExpired(lastInteractionAt: string | null): boolean {
  if (!lastInteractionAt) return false;
  const lastMs = new Date(lastInteractionAt).getTime();
  const nowMs = Date.now();
  const elapsedHours = (nowMs - lastMs) / (1000 * 60 * 60);
  return elapsedHours > AI_CHAT_CONFIG.SESSION_TIMEOUT_HOURS;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAiChatStore = create<AiChatState>()(
  persist(
    (set, get) => ({
      sessions: {},
      jobPositionSessions: {},

      // --- Generic ---

      getSession(key) {
        const session = get().sessions[key];
        if (!session || isSessionExpired(session.lastInteractionAt)) {
          return { ...EMPTY_SESSION };
        }
        return session;
      },

      setSession(key, patch) {
        set(state => ({
          sessions: {
            ...state.sessions,
            [key]: {
              ...(state.sessions[key] ?? EMPTY_SESSION),
              ...patch,
            },
          },
        }));
      },

      clearSession(key) {
        set(state => {
          const next = { ...state.sessions };
          delete next[key];
          return { sessions: next };
        });
      },

      // --- Backward-compat aliases (candidate) ---
      getCandidateSession(key) { return get().getSession(key); },
      setCandidateSession(key, patch) { get().setSession(key, patch); },
      clearCandidateSession(key) { get().clearSession(key); },

      // --- Job Position ---

      getJobPositionSession(key) {
        const session = get().jobPositionSessions[key];
        if (!session || isSessionExpired(session.lastInteractionAt)) {
          return { ...EMPTY_JOB_POSITION_SESSION };
        }
        return session;
      },

      setJobPositionSession(key, patch) {
        set(state => ({
          jobPositionSessions: {
            ...state.jobPositionSessions,
            [key]: {
              ...(state.jobPositionSessions[key] ?? EMPTY_JOB_POSITION_SESSION),
              ...patch,
            },
          },
        }));
      },

      clearJobPositionSession(key) {
        set(state => {
          const next = { ...state.jobPositionSessions };
          delete next[key];
          return { jobPositionSessions: next };
        });
      },
    }),
    {
      name: 'ai-chat-sessions', // localStorage key
      partialize: state => ({
        sessions: state.sessions,
        jobPositionSessions: state.jobPositionSessions,
      }),
    }
  )
);
